import { useState } from "react";
import { Link } from "react-router-dom";
import { trpc } from "../../lib/trpc.js";
import { dataHora } from "../../lib/formato.js";
import { Lista, Pagina } from "../../components/Pagina.js";
import { Botao, Falha, Girando, Selo, Vazio } from "../../components/ui.js";

const POR_PAGINA = 24;

/**
 * Os lotes da conta.
 *
 * Só agregados: quantas fotos, quantas prontas, quantas pedem atenção. Abrir um
 * lote é entrar nele — esta tela responde "o que está pendente", não "como está
 * cada foto".
 */
export function LotesPage() {
  const [pagina, setPagina] = useState(0);

  const lista = trpc.lotes.listar.useQuery({ limit: POR_PAGINA, offset: pagina * POR_PAGINA });

  const lotes = lista.data?.lotes ?? [];
  const total = lista.data?.total ?? 0;
  const paginas = Math.ceil(total / POR_PAGINA);

  return (
    <Pagina
      titulo="Lotes"
      descricao="Processe várias fotos de uma vez, com o mesmo personagem e o mesmo evento."
      acao={
        <Link to="/lotes/novo">
          <Botao tom="brasa">Novo lote</Botao>
        </Link>
      }
    >
      {lista.isError ? (
        <Falha mensagem={lista.error.message} tentarNovamente={() => void lista.refetch()} />
      ) : lista.isLoading ? (
        <div className="flex justify-center py-12">
          <Girando className="size-6 text-texto-fraco" />
        </div>
      ) : lotes.length === 0 ? (
        <Vazio
          titulo="Nenhum lote ainda"
          descricao="Um lote processa as fotos de um evento inteiro numa passada: você escolhe o local, a vivência, as fotos e o personagem, e acompanha aqui."
          acao={
            <Link to="/lotes/novo">
              <Botao tom="brasa">Criar o primeiro</Botao>
            </Link>
          }
        />
      ) : (
        <>
          <Lista
            itens={lotes}
            chave={(l) => l.loteId ?? ""}
            colunas={["Nº", "Lote", "Onde", "Quando", "Fotos", "Prontas", "Atenção", "Situação"]}
            linha={(l) => [
              <span key="numero" className="num text-texto-suave">
                {l.numero ?? "—"}
              </span>,
              <Link
                key="nome"
                to={`/lotes/${l.loteId}`}
                className="font-medium text-texto underline-offset-4 hover:underline"
              >
                {l.nome ?? "Sem nome"}
              </Link>,
              <span key="onde" className="text-texto-suave">
                {[l.instituicao, l.vivencia].filter(Boolean).join(" · ") || "—"}
              </span>,
              <span key="quando" className="text-texto-suave">
                {dataHora(l.criadoEm)}
              </span>,
              <span key="fotos" className="num">
                {l.fotos}
              </span>,
              <span key="prontas" className="num">
                {l.aprovadas + l.prontas}
              </span>,
              <span key="atencao" className="num">
                {l.suspeitas + l.falhadas}
              </span>,
              <Situacao key="situacao" {...l} />,
            ]}
            cartao={(l) => (
              <Link to={`/lotes/${l.loteId}`} className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate font-medium text-texto">
                    {l.numero !== null && (
                      <span className="num text-texto-fraco">#{l.numero} </span>
                    )}
                    {l.nome ?? "Sem nome"}
                  </span>
                  <span className="shrink-0 text-[0.75rem] text-texto-fraco">
                    {dataHora(l.criadoEm)}
                  </span>
                </div>
                {(l.instituicao ?? l.vivencia) !== null && (
                  <span className="truncate text-[0.8125rem] text-texto-suave">
                    {[l.instituicao, l.vivencia].filter(Boolean).join(" · ")}
                  </span>
                )}
                <div className="flex flex-wrap items-center gap-2 text-[0.8125rem] text-texto-suave">
                  <span className="num">{l.fotos} fotos</span>
                  <span aria-hidden>·</span>
                  <span className="num">{l.aprovadas + l.prontas} prontas</span>
                  <Situacao {...l} />
                </div>
              </Link>
            )}
          />

          {paginas > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <Botao tom="quieto" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>
                Anterior
              </Botao>
              <span className="text-[0.8125rem] text-texto-fraco">
                {pagina + 1} de {paginas}
              </span>
              <Botao
                tom="quieto"
                disabled={pagina + 1 >= paginas}
                onClick={() => setPagina((p) => p + 1)}
              >
                Próxima
              </Botao>
            </div>
          )}
        </>
      )}
    </Pagina>
  );
}

/**
 * Uma palavra sobre o lote inteiro.
 *
 * A ordem das perguntas é a da urgência: está andando? sobrou algo esperando
 * saldo? alguma pede conferência? Só depois "terminou".
 */
function Situacao({
  emCurso,
  semSaldo,
  suspeitas,
  falhadas,
  abortadas,
}: {
  emCurso: number;
  semSaldo: number;
  suspeitas: number;
  falhadas: number;
  abortadas: number;
}) {
  if (emCurso > 0) return <Selo tom="projecao">em andamento</Selo>;
  if (semSaldo > 0) return <Selo tom="quieto">esperando saldo</Selo>;
  if (suspeitas > 0) return <Selo tom="descartar">{suspeitas} para conferir</Selo>;
  if (falhadas > 0) return <Selo tom="descartar">{falhadas} falharam</Selo>;
  if (abortadas > 0) return <Selo tom="quieto">{abortadas} abortadas</Selo>;
  return <Selo tom="fixado">concluído</Selo>;
}
